import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Form, Row, Col } from "react-bootstrap";
import { Tooltip } from "react-tooltip"; // or use any tooltip library you like
import AsyncSelect from "react-select/async";
import { FaSearch } from "react-icons/fa";

import {
  MdAdd,
  MdDelete,
  MdVisibility,
  MdVisibilityOff,
  MdContentCopy,
  MdRefresh,
  MdVerified,
} from "react-icons/md";
import "../../styles/InputSheet.css";
import "../../styles/ReactSelect.css";
import "../../styles/FileUpload.css";
import { MdModeEditOutline } from "react-icons/md";
import { GoUnverified } from "react-icons/go";

import {
  FiPlusCircle,
  FiMinusCircle,
  FiUploadCloud,
  FiInfo,
} from "react-icons/fi";
import {
  addOemPartner,
  getOemPartners,
  editOemPartner,
  deleteOemPartner,
  changeOemStatus,
  getScanTypes,
  resendVerificationEmail,
} from "../../redux/actions/oemActions";
import { getCountries } from "../../redux/actions/countryActions";
import { getLicenseslist } from "../../redux/actions/itSalesActions";
import DeleteConfirmModal from "../../modals/DeleteConfirmModal";
import StatusConfirmModal from "../../modals/StatusConfirmModal";
import InactiveEditModal from "../../modals/InactiveEditModal";
import LogoPreviewModal from "../../modals/LogoPreviewModal";
import OEMDetailsOffcanvas from "../../../helper/OEMDetailsOffcanvas";
import Pagination from "../../../helper/Pagination";
import { toast } from "react-toastify";
import PlansDetails from "./common/PlansDetails";
import OEMPartnerTable from "./common/OEMPartnerTable";
import PasswordResetModal from "../../modals/PasswordResetModal";
import LicenseCreateModal from "../../modals/LicenseCreateModal";
import { getPhoneValidationRules } from "../../../utils/phoneValidation";

// Phone validation functions
const validateSGPhone = (number) => {
  const digitsOnly = number.replace(/[^0-9]/g, "");
  return digitsOnly.length === 8 && !digitsOnly.match(/^0+$/);
};

const validatePhoneNumber = (number, country) => {
  const digitsOnly = number.replace(/[^0-9]/g, "");

  if (!digitsOnly) return true; // Allow empty during typing

  // Check for all zeros pattern
  if (digitsOnly.match(/^0+$/)) {
    return "Invalid contact number";
  }

  // Singapore specific validation
  if (country === "Singapore") {
    if (digitsOnly.length > 0 && digitsOnly.length < 8) {
      return true; // Allow partial input during typing
    }
    if (digitsOnly.length === 8) {
      return validateSGPhone(number) || "Invalid Singapore number";
    }
    if (digitsOnly.length > 8) {
      return "Singapore contact number must be exactly 8 digits";
    }
    return true;
  }

  // General validation for other countries
  if (digitsOnly.length < 10) {
    return true; // Allow partial input during typing
  }
  if (digitsOnly.length > 15) {
    return "Contact number must be 10–15 digits";
  }

  return true;
};

const OEMPartners = () => {
  const dispatch = useDispatch();
  const token = sessionStorage.getItem("authToken");
  const {
    partners = [],
    scanTypes = [],
    loading = false,
    formLoading = false,
    scanTypesLoading = false,
    error = null,
    pagination = {},
    passwordResetStatus = null,
  } = useSelector((state) => state.oem || {});
  const { countries = [] } = useSelector((state) => state.country || {});
  const [currentPage, setCurrentPage] = useState(1);
  const {
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    register,
    watch,
    control,
    setValue,
  } = useForm({
    defaultValues: {
      scanTypeId: "",
      companyName: "",
      email: "",
      firstName: "",
      lastName: "",
      contactNumber: "",
      logo: null,
      country: "",
      currency: "",
      almataSharedDiscription: "",
      licenseCount: "",
      stripeProductId: [""],
      planDetails: [
        {
          stripeProductId: "",
          amount: "",
          planName: "",
          couponDetails: "",
        },
      ],
      oemUrl: "",
    },
  });

  const [showInputForm, setShowInputForm] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    partner: null,
  });
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    partner: null,
    newStatus: null,
  });
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchChange = (e) => {
    const value = e.target.value;
    // Prevent leading spaces but allow spaces after text
    if (value.length === 0 || (value.length > 0 && value[0] !== " ")) {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page when searching
    } else if (searchTerm.length > 0) {
      // Allow spaces if there's already text
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page when searching
    }
  };
  const [statusFilter, setStatusFilter] = useState("active");
  const [scanTypeFilter, setScanTypeFilter] = useState("");
  const [descriptionWordCount, setDescriptionWordCount] = useState(0);
  const [inactiveEditModal, setInactiveEditModal] = useState({
    isOpen: false,
    partner: null,
  });
  const [resetPassword, setResetPassword] = useState(false);
  const [passwordResetModal, setPasswordResetModal] = useState({
    isOpen: false,
    currentPassword: "",
  });
  const [stripeProductId, setStripeProductId] = useState([""]);
  const [planDetails, setPlanDetails] = useState([
    {
      stripeProductId: "",
      amount: "",
      planName: "",
      couponDetails: "",
    },
  ]);
  const [logoPreviewModal, setLogoPreviewModal] = useState({
    isOpen: false,
    imageUrl: "",
    imageName: "",
  });
  const [detailsOffcanvas, setDetailsOffcanvas] = useState({
    isOpen: false,
    partner: null,
  });
  const [selectedDomainType, setSelectedDomainType] = useState("");
  const [flagLink, setFlagLink] = useState("us");
  const [countryCode, setCountryCode] = useState("+1");
  const [licenseFields, setLicenseFields] = useState([{ id: 0, value: "" }]);
  const [licenseCreateModal, setLicenseCreateModal] = useState(false);
  const { licenses = [] } = useSelector((state) => state.itSales || {});

  const logoFile = watch("logo");
  const selectedCountry = watch("country");
  const almataDescription = watch("almataSharedDiscription");

  useEffect(() => {
    if (almataDescription) {
      const words = almataDescription
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0);
      setDescriptionWordCount(words.length);
    } else {
      setDescriptionWordCount(0);
    }
  }, [almataDescription]);

  const loadCountryOptions = async (inputValue) => {
    try {
      const response = inputValue
        ? await fetch(
            `https://restcountries.com/v3.1/name/${inputValue}?fields=name,currencies,cca2,cca3,ccn3,idd  `,
          )
        : await fetch(
            "https://restcountries.com/v3.1/all?fields=name,currencies,cca2,cca3,ccn3,idd",
          );
      const data = await response.json();
      return data.slice(0, 10).map((country) => {
        const currency = Object.keys(country.currencies || {})[0] || "USD";
        return {
          value: country.name.common,
          label: country.name.common,
          // currency: currency
          isoCode: country.cca2,
          countryCode: country.idd.root
            ? country.idd.root +
              (country.idd.suffixes?.length
                ? country.name.common === "United States"
                  ? ""
                  : country?.idd?.suffixes?.[0]
                : "")
            : "",
        };
      });
    } catch (error) {
      return [];
    }
  };

  const loadLicenseOptions = async (inputValue) => {
    return (Array.isArray(licenses) ? licenses : [])
      .filter((license) => {
        const matchesSearch =
          !inputValue ||
          license.name?.toLowerCase().includes(inputValue.toLowerCase());
        const isUnassigned = !license.assigned || license._id === editingPartner?.licenseId;
        return matchesSearch && isUnassigned;
      })
      .slice(0, 10)
      .map((license) => ({
        value: license._id,
        label: license.name,
        licenseData: license,
      }));
  };

  const handleMultiLicenseChange = (selectedOptions) => {
    const newFields = selectedOptions.map((option, index) => ({
      id: index,
      value: option.value,
    }));
    setLicenseFields(
      newFields.length > 0 ? newFields : [{ id: 0, value: "" }],
    );
    if (newFields.length > 0) {
      setValue("stripeLicenseKeyId", newFields[0].value);
      clearErrors("stripeLicenseKeyId");
    } else {
      setValue("stripeLicenseKeyId", "");
    }
  };

  const handleCountryChange = (selectedOption, action) => {
    if (action === "clear") {
      setValue("country", "");
      setValue("currency", "");
      setFlagLink("us");
      setCountryCode("+1");
      return null;
    }
    setValue("country", selectedOption?.value || "");

    // Set currency based on country
    let currency = "USD"; // default
    if (selectedOption?.value === "Japan") currency = "JPY";
    else if (selectedOption?.value === "Singapore") currency = "SGD";
    else if (selectedOption?.value === "Hong Kong") currency = "HKD";
    else if (selectedOption?.value === "Australia") currency = "AUD";
    else if (selectedOption?.value === "Austria") currency = "EUR";

    setValue("currency", currency);

    // Set flag and country code from selected option
    if (selectedOption) {
      setFlagLink(selectedOption.isoCode || "us");
      setCountryCode(selectedOption.countryCode || "+1");
    }

    return selectedOption;
  }

  useEffect(() => {
    dispatch(
      getOemPartners({
        limit: 10,
        page: currentPage,
        search: searchTerm,
        status: statusFilter,
        scanType: scanTypeFilter,
      }),
    );
    dispatch(getScanTypes());
    dispatch(getLicenseslist());
  }, [dispatch, currentPage, searchTerm, statusFilter, scanTypeFilter]);

  // Set default Single scan type when scanTypes are loaded
  useEffect(() => {
    if (scanTypes.length > 0 && !editingPartner && !selectedDomainType) {
      const singleScanType = scanTypes.find((type) => type.name === "Single");
      if (singleScanType) {
        setSelectedDomainType(singleScanType._id);
        setValue("scanTypeId", singleScanType._id);
      }
    }
  }, [scanTypes, editingPartner, selectedDomainType, setValue]);

  // Set default US country when not editing
  useEffect(() => {
    if (!editingPartner && !watch("country")) {
      setValue("country", "United States");
      setValue("currency", "USD");
      setFlagLink("us");
      setCountryCode("+1");
    }
  }, [editingPartner, setValue, watch]);

  const onSubmit = (data) => {
    const selectedScanType = scanTypes.find(
      (type) => type._id === data.scanTypeId,
    );
    const isSingleDomain = selectedScanType?.name === "Single";
    const isLicenseScanType = selectedScanType?.name === "License";

    // Extract stripeProductId from planDetails only for single domain
    const filteredIds = isSingleDomain
      ? planDetails
          .map((plan) => plan.stripeProductId)
          .filter((id) => id && id.trim())
      : [];

    // Check for duplicates (case sensitive) only for single domain
    if (isSingleDomain) {
      const duplicates = filteredIds.filter(
        (id, index) => filteredIds.indexOf(id) !== index,
      );
      if (duplicates.length > 0) {
        toast.error("Duplicate stripeProductId");
        return;
      }
    }

    const adminId = JSON.parse(sessionStorage.getItem("user"))?._id;
    const {
      planDetails: _,
      oemUrl: __,
      status: ___,
      logo: ____,
      stripeLicenseKeyId: _____,
      licenseCount: ______,
      ...formDataWithoutExcluded
    } = data;

    const payload = {
      ...formDataWithoutExcluded,
      countryCode,
      flagLink: `https://flagcdn.com/${flagLink?.toLowerCase()}.svg`,
      createdByModel: "user",
      createdBy: adminId,
      refreshParams: {
        limit: 10,
        page: currentPage,
        search: searchTerm,
        status: statusFilter,
        scanType: scanTypeFilter,
      },
    };
    if (isSingleDomain) {
      payload.stripeProductId = filteredIds;
    } else {
      delete payload.stripeProductId;
    }
    if (isLicenseScanType) {
      const licenseId = licenseFields.find(f => f.value)?.value;
      if (licenseId) payload.licenseId = licenseId;
      if (data.licenseCount) payload.count = data.licenseCount;
    }
    if (selectedFile) payload.logo = selectedFile;
    else if (editingPartner && editingPartner.logo) payload.logo = editingPartner.logo;
    if (editingPartner) payload.status = data.status;
    console.log(payload, "payload");
    if (resetPassword && editingPartner) {
      payload.resetPassword = true;
    }
    if (editingPartner) {
      dispatch(editOemPartner({ id: editingPartner._id, ...payload }));
    } else {
      dispatch(addOemPartner(payload));
    }
  };

  const { success } = useSelector((state) => state.oem || {});

  useEffect(() => {
    if (success && !formLoading) {
      reset({
        scanTypeId: "",
        companyName: "",
        email: "",
        firstName: "",
        lastName: "",
        contactNumber: "",
        logo: null,
        country: "",
        currency: "",
        status: "active",
        almataSharedDiscription: "",
        stripeProductId: [""],
        planDetails: [
          {
            stripeProductId: "",
            amount: "",
            planName: "",
            couponDetails: "",
          },
        ],
        oemUrl: "",
      });
      setSelectedFileName("");
      setSelectedFile(null);
      setEditingPartner(null);
      setStripeProductId([""]);
      setPlanDetails([
        {
          stripeProductId: "",
          amount: "",
          planName: "",
          couponDetails: "",
        },
      ]);
      setSelectedDomainType("");
      setLicenseFields([{ id: 0, value: "" }]);
    }
  }, [success, formLoading, reset]);

  // Separate useEffect for password reset success
  useEffect(() => {
    if (passwordResetStatus === 200) {
      dispatch(
        getOemPartners({
          limit: 10,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          scanType: scanTypeFilter,
        }),
      );
    }
  }, [
    passwordResetStatus,
    dispatch,
    currentPage,
    searchTerm,
    statusFilter,
    scanTypeFilter,
  ]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setSelectedFileName(file.name);
    } else {
      setSelectedFile(null);
      setSelectedFileName("");
    }
  };

  const removePartner = (partner) => {
    setDeleteModal({ isOpen: true, partner });
  };

  const handleDeleteConfirm = () => {
    dispatch(
      deleteOemPartner({
        id: deleteModal.partner._id,
        refreshParams: {
          limit: 10,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          scanType: scanTypeFilter,
        },
      }),
    );
    setDeleteModal({ isOpen: false, partner: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, partner: null });
  };

  const handleStatusChange = (partner) => {
    const newStatus = partner.status === "active" ? "inactive" : "active";
    setStatusModal({ isOpen: true, partner, newStatus });
  };

  const handleStatusConfirm = () => {
    dispatch(
      changeOemStatus({
        _id: statusModal.partner._id,
        status: statusModal.newStatus,
        refreshParams: {
          limit: 10,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          scanType: scanTypeFilter,
        },
      }),
    );
    setStatusModal({ isOpen: false, partner: null, newStatus: null });
  };

  const handleStatusCancel = () => {
    setStatusModal({ isOpen: false, partner: null, newStatus: null });
  };

  const handleEditClick = async (partner) => {
    if (partner.status === "inactive") {
      setInactiveEditModal({ isOpen: true, partner });
    } else {
      setEditingPartner(partner);
      reset({
        ...partner,
        oemUrl: partner.oemUrl || "",
      });
      setShowInputForm(true);
      setResetPassword(false);

      // Set flag and country code for existing partner
      if (partner.country) {
        try {
          const response = await fetch(
            `https://restcountries.com/v3.1/name/${partner.country}?fields=name,currencies,cca2,cca3,ccn3,idd`,
          );
          const data = await response.json();
          if (data && data.length > 0) {
            const country = data[0];
            setFlagLink(country.cca2 || "us");
            setCountryCode(
              country.idd.root
                ? country.idd.root +
                    (country.idd.suffixes?.length
                      ? country.name.common === "United States"
                        ? ""
                        : country?.idd?.suffixes?.[0]
                      : "")
                : "+1",
            );
          }
        } catch (error) {
          console.error("Error fetching country data:", error);
          setFlagLink("us");
          setCountryCode("+1");
        }
      } else {
        // Set default values if no country is set
        setFlagLink("us");
        setCountryCode("+1");
      }

      if (partner.scanType && scanTypes.length > 0) {
        const scanType = scanTypes.find(
          (type) => type.name === partner.scanType,
        );
        if (scanType) {
          setSelectedDomainType(scanType._id);
          setValue("scanTypeId", scanType._id);
          if (scanType.name === "License" && partner.licenseId) {
            setLicenseFields([{ id: 0, value: partner.licenseId }]);
            setValue("licenseCount", partner.count || partner.licenseCount || "");
          }
        }
      }
      // Ensure oemUrl is set
      setValue("oemUrl", partner.oemUrl || "");
      setStripeProductId(
        Array.isArray(partner.stripeProductId) ? partner.stripeProductId : [""],
      );
      setPlanDetails(
        partner.planDetails &&
          Array.isArray(partner.planDetails) &&
          partner.planDetails.length > 0
          ? partner.planDetails.map((plan) => ({
              stripeProductId: plan.stripeProductId || "",
              amount: plan.amount || "",
              planName: plan.planName || "",
              couponDetails: plan.couponDetails || "",
              currency: plan.currency || partner.currency || "USD",
            }))
          : [
              {
                stripeProductId: "",
                amount: "",
                planName: "",
                couponDetails: "",
              },
            ],
      );
    }
  };

  const handleInactiveEditClose = async () => {
    const partner = inactiveEditModal.partner;
    setInactiveEditModal({ isOpen: false, partner: null });
    // After closing modal, show the form with disabled inputs
    if (partner) {
      setEditingPartner(partner);
      reset({
        ...partner,
        oemUrl: partner.oemUrl || "",
      });
      setShowInputForm(true);
      setResetPassword(false);

      // Set flag and country code for existing partner
      if (partner.country) {
        try {
          const response = await fetch(
            `https://restcountries.com/v3.1/name/${partner.country}?fields=name,currencies,cca2,cca3,ccn3,idd`,
          );
          const data = await response.json();
          if (data && data.length > 0) {
            const country = data[0];
            setFlagLink(country.cca2 || "us");
            setCountryCode(
              country.idd.root
                ? country.idd.root +
                    (country.idd.suffixes?.length
                      ? country.name.common === "United States"
                        ? ""
                        : country?.idd?.suffixes?.[0]
                      : "")
                : "+1",
            );
          }
        } catch (error) {
          console.error("Error fetching country data:", error);
          setFlagLink("us");
          setCountryCode("+1");
        }
      } else {
        // Set default values if no country is set
        setFlagLink("us");
        setCountryCode("+1");
      }

      if (partner.scanType && scanTypes.length > 0) {
        const scanType = scanTypes.find(
          (type) => type.name === partner.scanType,
        );
        if (scanType) {
          setSelectedDomainType(scanType._id);
          setValue("scanTypeId", scanType._id);
          if (scanType.name === "License" && partner.licenseId) {
            setLicenseFields([{ id: 0, value: partner.licenseId }]);
            setValue("licenseCount", partner.count || partner.licenseCount || "");
          }
        }
      }
      // Ensure oemUrl is set
      setValue("oemUrl", partner.oemUrl || "");
      setStripeProductId(
        Array.isArray(partner.stripeProductId) ? partner.stripeProductId : [""],
      );
      setPlanDetails(
        partner.planDetails &&
          Array.isArray(partner.planDetails) &&
          partner.planDetails.length > 0
          ? partner.planDetails.map((plan) => ({
              stripeProductId: plan.stripeProductId || "",
              amount: plan.amount || "",
              planName: plan.planName || "",
              couponDetails: plan.couponDetails || "",
              currency: plan.currency || partner.currency || "USD",
            }))
          : [
              {
                stripeProductId: "",
                amount: "",
                planName: "",
                couponDetails: "",
              },
            ],
      );
    }
  };

  const handlePasswordResetClick = () => {
    // Open modal with the current password from the form field
    const currentPasswordValue =
      watch("plainPassword") || editingPartner?.plainPassword || "";
    setPasswordResetModal({
      isOpen: true,
      currentPassword: currentPasswordValue,
    });
  };

  const handlePasswordResetSave = (newPassword) => {
    setValue("plainPassword", newPassword);
    setPasswordResetModal({ isOpen: false, currentPassword: "" });
  };

  const handlePasswordResetClose = () => {
    setPasswordResetModal({ isOpen: false, currentPassword: "" });
  };

  const handleLogoPreview = () => {
    let imageUrl = "";
    let imageName = "";

    if (selectedFile) {
      imageUrl = URL.createObjectURL(selectedFile);
      imageName = selectedFile.name;
    } else if (editingPartner?.logo) {
      imageUrl = editingPartner.logo;
      imageName = editingPartner.logo.split("/").pop();
    }

    setLogoPreviewModal({
      isOpen: true,
      imageUrl,
      imageName,
    });
  };

  const handleLogoPreviewClose = () => {
    setLogoPreviewModal({
      isOpen: false,
      imageUrl: "",
      imageName: "",
    });
  };

  const handleViewDetails = (partner) => {
    setDetailsOffcanvas({
      isOpen: true,
      partner,
    });
  };

  const handleResendVerification = (oemId) => {
    dispatch(resendVerificationEmail(oemId));
  };
  const handleDetailsOffcanvasClose = () => {
    setDetailsOffcanvas({
      isOpen: false,
      partner: null,
    });
  };
  return (
    <div className="page-container">
      <div className="page-card scroll-container">
        <div className="form-header">
          <h2>
            OEM Partners Management{" "}
            {selectedDomainType &&
              scanTypes.find((type) => type._id === selectedDomainType) &&
              `(${
                scanTypes.find((type) => type._id === selectedDomainType).name
              } Scan)`}
          </h2>
          <div
            onClick={() => setShowInputForm(!showInputForm)}
            className="cursor-pointer info_pointer toggle-btn fs-5 fw-bolder"
            title={showInputForm ? "Hide Input Form" : "Show Input Form"}
          >
            {showInputForm ? <FiMinusCircle /> : <FiPlusCircle />}
          </div>
        </div>
        {showInputForm && (
          <div className="input-form-section">
            <form onSubmit={handleSubmit(onSubmit)} className="input-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>
                    Scan Type <span className="text-danger">*</span>
                  </label>
                  <select
                    {...register("scanTypeId", {
                      required: "Scan type is required",
                    })}
                    className={`form-input ${errors.scanTypeId ? "error" : ""}`}
                    disabled={
                      editingPartner?.status === "inactive" || editingPartner
                    }
                    style={{
                      backgroundColor:
                        editingPartner?.status === "inactive" || editingPartner
                          ? "#f8f9fa"
                          : "",
                      appearance: editingPartner ? "none" : "auto",
                      WebkitAppearance: editingPartner ? "none" : "auto",
                      MozAppearance: editingPartner ? "none" : "auto",
                    }}
                    onChange={(e) => {
                      setSelectedDomainType(e.target.value);
                      setValue("scanTypeId", e.target.value);
                    }}
                  >
                    {scanTypes.map((scanType) => (
                      <option key={scanType._id} value={scanType._id}>
                        {scanType.name} Scan
                      </option>
                    ))}
                  </select>
                  {errors.scanTypeId && (
                    <span className="error-text">
                      {errors.scanTypeId.message}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Company Name <span className="text-danger">*</span>
                  </label>
                  <input
                    {...register("companyName", {
                      required: "Company name is required",
                      validate: (value) =>
                        value.trim() !== "" || "Company name is required",
                    })}
                    className={`form-input ${errors.companyName ? "error" : ""}`}
                    placeholder="Enter company name"
                    disabled={editingPartner?.status === "inactive"}
                    onChange={(e) => {
                      const trimmedValue = e.target.value.replace(/\s+/g, " ");
                      setValue("companyName", trimmedValue);
                    }}
                    style={{
                      backgroundColor:
                        editingPartner?.status === "inactive" ? "#f8f9fa" : "",
                    }}
                  />
                  {errors.companyName && (
                    <span className="error-text">
                      {errors.companyName.message}
                    </span>
                  )}
                </div>

                <div className="form-group" style={{ position: "relative" }}>
                  <label>
                    Email <span className="text-danger">*</span>{" "}
                  </label>
                  <input
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Please enter a valid email address",
                      },
                    })}
                    type="email"
                    className={`form-input ${errors.email ? "error" : ""}`}
                    placeholder="Enter email"
                    disabled={
                      editingPartner?.status === "inactive" || editingPartner
                    }
                    style={{
                      backgroundColor:
                        editingPartner?.status === "inactive"
                          ? "#f8f9fa"
                          : "" || editingPartner
                            ? "#f8f9fa"
                            : "",
                      paddingRight: "35px",
                    }}
                  />
                  {editingPartner &&
                    (editingPartner.verified ? (
                      <MdVerified
                        className="position-absolute"
                        style={{
                          right: "5px",
                          top: "66%",
                          transform: "translateY(-50%)",
                          color: "#28a745",
                          fontSize: "18px",
                          cursor: "pointer",
                        }}
                        title="Email Verified"
                      />
                    ) : (
                      <GoUnverified
                        className="position-absolute"
                        style={{
                          right: "5px",
                          top: "66%",
                          transform: "translateY(-50%)",
                          color: "#dc3545",
                          fontSize: "18px",
                          cursor: "pointer",
                        }}
                        title="Email Not Verified"
                      />
                    ))}
                  {errors.email && (
                    <span className="error-text">{errors.email.message}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    First Name <span className="text-danger">*</span>
                  </label>
                  <input
                    {...register("firstName", {
                      required: "First name is required",
                      validate: (value) =>
                        value.trim() !== "" || "First name is required",
                    })}
                    className={`form-input ${errors.firstName ? "error" : ""}`}
                    placeholder="Enter first name"
                    disabled={editingPartner?.status === "inactive"}
                    style={{
                      backgroundColor:
                        editingPartner?.status === "inactive" ? "#f8f9fa" : "",
                    }}
                  />
                  {errors.firstName && (
                    <span className="error-text">
                      {errors.firstName.message}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Last Name <span className="text-danger">*</span>
                  </label>
                  <input
                    {...register("lastName", {
                      required: "Last Name is required",
                      validate: (value) =>
                        value.trim() !== "" || "Last Name is required",
                    })}
                    className={`form-input ${errors.firstName ? "error" : ""}`}
                    placeholder="Enter last name"
                    disabled={editingPartner?.status === "inactive"}
                    style={{
                      backgroundColor:
                        editingPartner?.status === "inactive" ? "#f8f9fa" : "",
                    }}
                  />
                  {errors.lastName && (
                    <span className="error-text">
                      {errors.lastName.message}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>
                    Country/Currency <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="country"
                    control={control}
                    rules={{ required: "Country is required" }}
                    render={({ field: { onChange, value, ...field } }) => (
                      <AsyncSelect
                        {...field}
                        value={
                          value
                            ? {
                                value,
                                label: `${value} (${
                                  watch("currency") || "USD"
                                })`,
                              }
                            : null
                        }
                        loadOptions={loadCountryOptions}
                        onChange={(selectedOption, action) => {
                          const result = handleCountryChange(
                            selectedOption,
                            action,
                          );
                          onChange(selectedOption?.value || "");
                        }}
                        placeholder="Search country..."
                        className={errors.country ? "error" : ""}
                        classNamePrefix="react-select"
                        isClearable
                        defaultOptions
                        noOptionsMessage={() => "No countries found"}
                        loadingMessage={() => "Loading countries..."}
                        isDisabled={editingPartner?.status === "inactive"}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            minHeight: "30px",
                            height: "30px",
                            backgroundColor:
                              editingPartner?.status === "inactive"
                                ? "#f8f9fa"
                                : base.backgroundColor,
                            border: errors.country
                              ? "1px solid #dc3545"
                              : "1px solid #ddd",
                            borderRadius: "4px",
                            "&:hover": {
                              border: errors.country
                                ? "1px solid #dc3545"
                                : "1px solid #ddd",
                            },
                          }),
                        }}
                      />
                    )}
                  />
                  <input {...register("currency")} type="hidden" />
                  {errors.country && (
                    <span className="error-text">{errors.country.message}</span>
                  )}
                </div>
                <div className="form-group" style={{ position: "relative" }}>
                  <label>
                    Contact Number <span className="text-danger">*</span>
                  </label>

                  {/* Input Wrapper */}
                  <div style={{ position: "relative" }}>
                    {/* Flag Icon + Country Code */}
                    {flagLink && countryCode && (
                      <div
                        className="position-absolute d-flex align-items-center"
                        style={{
                          left: "8px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 2,
                          borderRight: "1px solid #ddd",
                          paddingRight: "8px",
                          height: "24px",
                          gap: "4px",
                          background: "#fff",
                        }}
                      >
                        <img
                          src={`https://flagcdn.com/${flagLink?.toLowerCase()}.svg`}
                          alt="flag"
                          width={20}
                          height={14}
                          style={{ objectFit: "cover" }}
                        />
                        <span style={{ fontSize: "12px", color: "#666" }}>
                          ({countryCode})
                        </span>
                      </div>
                    )}

                    {/* Input Field */}
                    <input
                      {...register("contactNumber", {
                        required: "Contact number is required",
                        validate: (value) =>
                          validatePhoneNumber(value, selectedCountry),
                      })}
                      type="tel"
                      className={`form-input ${errors.contactNumber ? "error" : ""}`}
                      placeholder="Enter contact number"
                      maxLength={(() => {
                        const rules = getPhoneValidationRules(selectedCountry);
                        return rules.max;
                      })()}
                      onInput={(e) => {
                        let value = e.target.value.replace(/[^0-9]/g, "");
                        const rules = getPhoneValidationRules(selectedCountry);

                        // Prevent input beyond max length
                        if (value.length > rules.max) {
                          value = value.slice(0, rules.max);
                        }

                        e.target.value = value;
                        setValue("contactNumber", value);
                      }}
                      disabled={editingPartner?.status === "inactive"}
                      style={{
                        paddingLeft: flagLink && countryCode ? "85px" : "12px",
                        height: "30px",
                        border: errors.contactNumber
                          ? "1px solid #dc3545"
                          : "1px solid #ddd",
                        borderRadius: "4px",
                        backgroundColor:
                          editingPartner?.status === "inactive"
                            ? "#f8f9fa"
                            : "#fff",
                        width: "100%",
                      }}
                    />
                  </div>

                  {/* Error Text (absolute to avoid layout shift) */}
                  {errors.contactNumber && (
                    <span
                      className="error-text"
                      style={{
                        position: "absolute",
                        bottom: "-18px",
                        left: flagLink && countryCode ? "0" : "0",
                        fontSize: "12px",
                        color: "#ef4444",
                      }}
                    >
                      {errors.contactNumber.message}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>Logo</label>
                  <div className="file-input-wrapper d-flex align-items-center">
                    <div className="flex-grow-1 position-relative">
                      <input
                        type="file"
                        accept="image/*"
                        className="file-input"
                        id="logo-upload"
                        onChange={handleFileChange}
                        disabled={editingPartner?.status === "inactive"}
                        style={{
                          backgroundColor:
                            editingPartner?.status === "inactive"
                              ? "#f8f9fa"
                              : "",
                        }}
                      />
                      <label htmlFor="logo-upload" className="file-input-label">
                        <span>
                          {(() => {
                            const fileName =
                              selectedFileName ||
                              (editingPartner?.logo
                                ? editingPartner.logo.split("/").pop()
                                : "Upload Image");
                            return fileName.length > 22
                              ? fileName.substring(0, 22) + "..."
                              : fileName;
                          })()}
                        </span>
                        {!(selectedFile || editingPartner?.logo) && (
                          <FiUploadCloud className="upload-icon" />
                        )}
                      </label>

                      {/* Eye icon moved outside the file input label */}
                      {(selectedFile || editingPartner?.logo) && (
                        <MdVisibility
                          className="info_pointer text-secondary text-primary"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleLogoPreview();
                          }}
                          title="Preview Logo"
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: "22px",
                            // zIndex: 9999,
                            background: "white",
                            padding: "4px",
                            borderRadius: "4px",
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
                {scanTypes.find((type) => type._id === selectedDomainType)
                  ?.name === "License" && (
                  <div className="form-group">
                    <label>
                      License Count <span className="text-danger">*</span>
                    </label>
                    <div className="licensecounter-wapper">
                      <button
                        type="button"
                        className="licensecounter-btn"
                        onClick={() =>
                          setValue(
                            "licenseCount",
                            Math.max((watch("licenseCount") || 0) - 1, 0),
                          )
                        }
                      >
                        -
                      </button>

                      <input
                        {...register("licenseCount", {
                          required: "License count is required",
                          pattern: {
                            value: /^[0-9]+$/,
                            message: "Please enter a valid number",
                          },
                          value: 1,
                        })}
                        type="text"
                        className={`form-input text-center w-100 ${
                          errors.licenseCount ? "error" : ""
                        }`}
                      />

                      <button
                        type="button"
                        className="licensecounter-btn"
                        onClick={() =>
                          setValue(
                            "licenseCount",
                            (Number(watch("licenseCount")) || 0) + 1,
                          )
                        }
                      >
                        +
                      </button>
                    </div>

                    {errors.licenseCount && (
                      <span className="error-text">
                        {errors.licenseCount.message}
                      </span>
                    )}
                  </div>
                )}
                {scanTypes.find((type) => type._id === selectedDomainType)
                  ?.name === "License" && (
                  <div className="form-group">
                    <label>
                      License Keys <span className="text-danger">*</span>
                    </label>
                    <AsyncSelect
                      key={licenses.length}
                      isMulti
                      isDisabled={!!editingPartner}
                      value={licenseFields
                        .filter((field) => field.value)
                        .map((field) => {
                          const selectedLicense = licenses.find(
                            (l) => l._id === field.value,
                          );
                          if (selectedLicense) {
                            return {
                              value: selectedLicense._id,
                              label: selectedLicense.name,
                            };
                          }
                          if (editingPartner?.licenseId) {
                            const partnerLicense = licenses.find(
                              (l) => l._id === editingPartner.licenseId,
                            );
                            if (partnerLicense) {
                              return {
                                value: partnerLicense._id,
                                label: partnerLicense.name,
                              };
                            }
                          }
                          return null;
                        })
                        .filter(Boolean)}
                      loadOptions={loadLicenseOptions}
                      onChange={(selectedOptions) => {
                        handleMultiLicenseChange(selectedOptions || []);
                      }}
                      placeholder="Search and select licenses..."
                      className={errors.stripeLicenseKeyId ? "error" : ""}
                      classNamePrefix="react-select"
                      isClearable
                      defaultOptions={true}
                      noOptionsMessage={() => "No licenses found"}
                      loadingMessage={() => "Loading licenses..."}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      components={{
                        Option: ({
                          children,
                          innerProps,
                          isSelected,
                          isFocused,
                        }) => (
                          <div
                            {...innerProps}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 12px",
                              cursor: "pointer",
                              backgroundColor: isFocused ? "#f8f9fa" : "white",
                              opacity: isSelected ? 0.6 : 1,
                              pointerEvents: isSelected ? "none" : "auto",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              readOnly
                              style={{
                                marginRight: "8px",
                                cursor: isSelected ? "not-allowed" : "pointer",
                              }}
                            />
                            <span
                              style={{
                                color: isSelected ? "#6c757d" : "#212529",
                                textDecoration: isSelected
                                  ? "line-through"
                                  : "none",
                              }}
                            >
                              {children}
                            </span>
                          </div>
                        ),
                        MenuList: ({ children, innerRef, innerProps }) => (
                          <div ref={innerRef} {...innerProps}>
                            {children}
                            <div
                              onClick={() => setLicenseCreateModal(true)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "8px 12px",
                                cursor: "pointer",
                                backgroundColor: "#f8f9fa",
                                borderTop: "1px solid #dee2e6",
                                color: "#007bff",
                                fontWeight: "500",
                              }}
                            >
                              <FiPlusCircle
                                style={{ marginRight: "8px", fontSize: "14px" }}
                              />
                              <span>Add More License Keys</span>
                            </div>
                          </div>
                        ),
                      }}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "40px",
                          height: "40px",
                                                  width: "16vw",
                        maxWidth: "16vw",

                          border: errors.stripeLicenseKeyId
                            ? "1px solid #dc3545"
                            : "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: editingPartner ? "#f8f9fa" : "white",
                          cursor: editingPartner ? "not-allowed" : "default",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          display: "flex",
                          flexWrap: "nowrap",
                          overflowX: "auto",
                          overflowY: "hidden",
                          height: "30px",
                          width: "calc(100% - 40px)",
                          maxWidth: "calc(100% - 40px)",
                          padding: "2px 8px",
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                          "&::-webkit-scrollbar": {
                            display: "none",
                          },
                        }),
                        multiValue: (base) => ({
                          ...base,
                          flexShrink: 0,
                          margin: "1px 2px",
                          fontSize: "13px",
                        }),
                        multiValueLabel: (base) => ({
                          ...base,
                          whiteSpace: "nowrap",
                          padding: "2px 6px",
                        }),
                      }}
                    />
                    <Controller
                      name="stripeLicenseKeyId"
                      control={control}
                      rules={{
                        validate: () =>
                          licenseFields.some((f) => f.value) ||
                          "At least one license is required",
                      }}
                      render={() => null}
                    />

                    {errors.stripeLicenseKeyId && (
                      <span className="error-text">
                        {errors.stripeLicenseKeyId.message}
                      </span>
                    )}
                  </div>
                )}
                <div className="form-group">
                  <label>
                    Status <span className="text-danger">*</span>
                  </label>
                  <select
                    {...register("status", {
                      required: "Status is required",
                    })}
                    className={`form-input disabled`}
                    disabled
                    value={editingPartner ? watch("status") : "active"}
                    style={{
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {errors.status && (
                    <span className="error-text">{errors.status.message}</span>
                  )}
                </div>

                {editingPartner && (
                  <div className="form-group position-relative">
                    <label>Password</label>
                    <input
                      {...register("plainPassword")}
                      type="text"
                      className="form-input"
                      placeholder="Generated password"
                      style={{
                        backgroundColor:
                          editingPartner?.status === "inactive"
                            ? "#f8f9fa"
                            : "",
                      }}
                      readOnly
                    />
                    {editingPartner?.status === "active" && (
                      <MdRefresh
                        className="position-absolute"
                        style={{
                          right: "8px",
                          top: "66%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: "#007bff",
                          fontSize: "16px",
                          zIndex: 10,
                        }}
                        onClick={handlePasswordResetClick}
                        title="Reset password"
                      />
                    )}
                  </div>
                )}
                {editingPartner && editingPartner.scanType !== "Multiple" && (
                  <div className="form-group position-relative">
                    <label>OEM URL</label>
                    <div className="d-flex align-items-center">
                      <input
                        {...register("oemUrl")}
                        className={`form-input flex-grow-1 ${
                          editingPartner?.status === "inactive"
                            ? "disabled"
                            : ""
                        }`}
                        placeholder="OEM URL"
                        readOnly
                        style={{
                          backgroundColor: "#f8f9fa",
                          paddingRight: "40px",
                        }}
                      />
                      {watch("oemUrl") && (
                        <MdContentCopy
                          className="position-absolute"
                          style={{
                            right: "8px",
                            top: "68%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: copiedUrl ? "#28a745" : "#007bff",
                            fontSize: "13px",
                            zIndex: 0,
                          }}
                          onClick={() => {
                            const text = watch("oemUrl");
                            if (
                              typeof navigator !== "undefined" &&
                              navigator.clipboard &&
                              window.isSecureContext
                            ) {
                              navigator.clipboard
                                .writeText(text)
                                .then(() => {
                                  setCopiedUrl(true);
                                  setTimeout(() => setCopiedUrl(false), 2000);
                                })
                                .catch(() => {
                                  // fallback
                                  const textArea =
                                    document.createElement("textarea");
                                  textArea.value = text;
                                  textArea.style.position = "fixed";
                                  document.body.appendChild(textArea);
                                  textArea.focus();
                                  textArea.select();
                                  document.execCommand("copy");
                                  document.body.removeChild(textArea);

                                  setCopiedUrl(true);
                                  setTimeout(() => setCopiedUrl(false), 2000);
                                });
                            } else {
                              // fallback always for http
                              const textArea =
                                document.createElement("textarea");
                              textArea.value = text;
                              textArea.style.position = "fixed";
                              document.body.appendChild(textArea);
                              textArea.focus();
                              textArea.select();
                              document.execCommand("copy");
                              document.body.removeChild(textArea);

                              setCopiedUrl(true);
                              setTimeout(() => setCopiedUrl(false), 2000);
                            }
                          }}
                          title={copiedUrl ? "Copied!" : "Copy URL"}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="form-group col-4 mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <label>
                      Almata Shared Description
                      <span className="text-danger">*</span>
                      <span
                        className="text-secondary info_pointer cursor-pointer ms-1"
                        title="You can enter up to 50 words"
                      >
                        <FiInfo />
                      </span>
                    </label>
                  </div>
                  <div className="text-muted small mt-1">
                    Words: {descriptionWordCount}/50
                  </div>
                </div>

                <textarea
                  {...register("almataSharedDiscription", {
                    required: "Almata shared description is required",
                  })}
                  className={`form-input ${
                    errors.almataSharedDiscription ? "error" : ""
                  } ${editingPartner?.status === "inactive" ? "disabled" : ""}`}
                  placeholder="Enter OEM VOC...."
                  rows={4}
                  disabled={editingPartner?.status === "inactive"}
                  style={{
                    backgroundColor:
                      editingPartner?.status === "inactive" ? "#f8f9fa" : "",
                  }}
                  onChange={(e) => {
                    const words = e.target.value
                      .trim()
                      .split(/\s+/)
                      .filter((word) => word.length > 0);
                    setDescriptionWordCount(words.length);
                    // Clear validation error when user inputs valid data
                    if (e.target.value.trim()) {
                      clearErrors("almataSharedDiscription");
                    }
                  }}
                  onKeyDown={(e) => {
                    const currentText = e.target.value;
                    const words = currentText
                      .trim()
                      .split(/\s+/)
                      .filter((word) => word.length > 0);
                    if (
                      words.length >= 50 &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      !e.key.includes("Arrow")
                    ) {
                      e.preventDefault();
                      toast.error("Maximum 50 words reached");
                    }
                  }}
                  onPaste={(e) => {
                    const currentText = e.target.value;
                    const pastedText = e.clipboardData.getData("text");
                    const combinedText = currentText + pastedText;
                    const words = combinedText
                      .trim()
                      .split(/\s+/)
                      .filter((word) => word.length > 0);
                    if (words.length > 50) {
                      e.preventDefault();
                      toast.error("Cannot paste - would exceed 50 word limit");
                    }
                  }}
                />
                {errors.almataSharedDiscription && (
                  <span className="error-text">
                    {errors.almataSharedDiscription.message}
                  </span>
                )}
              </div>
              {/* Plan Details Section - Only show for single domain */}
              {scanTypes.find((type) => type._id === selectedDomainType)
                ?.name === "Single" && (
                <PlansDetails
                  planDetails={planDetails}
                  setPlanDetails={setPlanDetails}
                  register={register}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  errors={errors}
                  editingPartner={editingPartner}
                />
              )}

              <div className="form-actions">
                {editingPartner && (
                  <button
                    type="button"
                    className="cancel-btn-deleteModal"
                    onClick={() => {
                      setEditingPartner(null);
                      reset({
                        scanTypeId: "",
                        companyName: "",
                        email: "",
                        firstName: "",
                        lastName: "",
                        contactNumber: "",
                        logo: null,
                        country: "",
                        currency: "",
                        status: "active",
                        almataSharedDiscription: "",
                        stripeProductId: [""],
                        planDetails: [
                          {
                            stripeProductId: "",
                            amount: "",
                            planName: "",
                            couponDetails: "",
                          },
                        ],
                        oemUrl: "",
                      });
                      setSelectedFileName("");
                      setSelectedFile(null);
                      setStripeProductId([""]);
                      setPlanDetails([
                        {
                          stripeProductId: "",
                          amount: "",
                          planName: "",
                          couponDetails: "",
                        },
                      ]);
                      setSelectedDomainType("");
                    }}
                  >
                    Reset
                  </button>
                )}
                <button
                  type="submit"
                  className="add-btn"
                  disabled={
                    formLoading || editingPartner?.status === "inactive"
                  }
                >
                  {formLoading
                    ? "Processing..."
                    : editingPartner
                      ? "Update OEM"
                      : "Add OEM"}
                </button>
              </div>
            </form>
          </div>
        )}
        <OEMPartnerTable
          partners={partners}
          loading={loading}
          scanTypeFilter={scanTypeFilter}
          setScanTypeFilter={setScanTypeFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          searchTerm={searchTerm}
          handleSearchChange={handleSearchChange}
          scanTypes={scanTypes}
          copiedId={copiedId}
          setCopiedId={setCopiedId}
          handleViewDetails={handleViewDetails}
          handleEditClick={handleEditClick}
          removePartner={removePartner}
          handleStatusChange={handleStatusChange}
          currentPage={currentPage}
          pagination={pagination}
          setCurrentPage={setCurrentPage}
          handleResendVerification={handleResendVerification}
        />
      </div>

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        partnerName={deleteModal.partner?.companyName || ""}
      />

      <StatusConfirmModal
        isOpen={statusModal.isOpen}
        onClose={handleStatusCancel}
        onConfirm={handleStatusConfirm}
        partnerName={statusModal.partner?.companyName || ""}
        currentStatus={statusModal.partner?.status || ""}
        newStatus={statusModal.newStatus || ""}
      />

      <InactiveEditModal
        isOpen={inactiveEditModal.isOpen}
        onClose={handleInactiveEditClose}
        partnerName={inactiveEditModal.partner?.companyName || ""}
      />

      <PasswordResetModal
        isOpen={passwordResetModal.isOpen}
        onClose={handlePasswordResetClose}
        onSave={handlePasswordResetSave}
        currentPassword={passwordResetModal.currentPassword}
        partnerId={editingPartner?._id}
        partnerType="oem"
      />

      <LogoPreviewModal
        isOpen={logoPreviewModal.isOpen}
        onClose={handleLogoPreviewClose}
        imageUrl={logoPreviewModal.imageUrl}
        imageName={logoPreviewModal.imageName}
      />

      <LicenseCreateModal
        isOpen={licenseCreateModal}
        onClose={() => setLicenseCreateModal(false)}
        onLicenseCreated={() => {
          dispatch(getScanTypes());
          setTimeout(() => {
            setLicenseFields([...licenseFields]);
          }, 500);
        }}
      />

      <OEMDetailsOffcanvas
        isOpen={detailsOffcanvas.isOpen}
        onClose={handleDetailsOffcanvasClose}
        partner={detailsOffcanvas.partner}
      />
    </div>
  );
};

export default OEMPartners;
